import { useCallback, useRef } from "react";
import { cn } from "@/lib/utils";

interface TouchInteractionsProps {
  children: React.ReactNode;
  className?: string;
  onTouchStart?: () => void;
  onTouchEnd?: () => void;
  haptic?: boolean;
  ripple?: boolean;
}

export const TouchInteractions = ({ 
  children, 
  className, 
  onTouchStart, 
  onTouchEnd, 
  haptic = true,
  ripple = true 
}: TouchInteractionsProps) => {
  const rippleRef = useRef<HTMLDivElement>(null);
  
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Haptic feedback simulation
    if (haptic && 'vibrate' in navigator) {
      navigator.vibrate(10);
    }
    
    // Ripple effect - optimized to avoid forced reflow
    if (ripple && rippleRef.current) {
      const touch = e.touches[0];
      const clientX = touch.clientX;
      const clientY = touch.clientY;
      const container = rippleRef.current;
      
      // Use requestAnimationFrame to batch DOM read/write operations
      requestAnimationFrame(() => {
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        
        const rippleEl = document.createElement('div');
        rippleEl.className = 'absolute rounded-full bg-primary/20 pointer-events-none animate-ping';
        rippleEl.style.cssText = `left:${x}px;top:${y}px;transform:translate(-50%,-50%);width:20px;height:20px`;
        
        container.appendChild(rippleEl);
        
        setTimeout(() => {
          rippleEl.remove();
        }, 600);
      });
    }
    
    onTouchStart?.();
  }, [haptic, ripple, onTouchStart]);

  const handleTouchEnd = useCallback(() => {
    onTouchEnd?.();
  }, [onTouchEnd]);

  return (
    <div
      ref={rippleRef}
      className={cn(
        "relative overflow-hidden touch-target active:scale-95 transition-transform duration-100",
        className
      )}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {children}
    </div>
  );
};